import { useEffect, useRef } from 'react';
import type { MediaPipeLandmark } from '@/types/mediapipe';
import { drawPoseLandmarks, drawPoseConnections } from '@/services/canvasUtils';
import { setupCanvas } from '@/utils/canvasHelpers';
import { cn } from '@/utils/cn';

interface PoseOverlayProps {
  landmarks: MediaPipeLandmark[] | null;
  width: number;
  height: number;
  landmarkColor?: string;
  connectionColor?: string;
  showLandmarks?: boolean;
  showConnections?: boolean;
  className?: string;
}

function PoseOverlay({
  landmarks,
  width,
  height,
  landmarkColor = '#00ff00',
  connectionColor = '#00ff00',
  showLandmarks = true,
  showConnections = true,
  className,
}: PoseOverlayProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const ctx = setupCanvas(canvasRef, width, height);
    if (!ctx) return;

    try {
      // Draw pose if landmarks exist
      if (landmarks && landmarks.length > 0) {
        if (showConnections) {
          drawPoseConnections(ctx, landmarks, width, height, connectionColor);
        }
        if (showLandmarks) {
          drawPoseLandmarks(ctx, landmarks, width, height, landmarkColor);
        }
      }
    } catch (error) {
      console.error('[PoseOverlay] Canvas drawing error:', error);
    }
  }, [landmarks, width, height, landmarkColor, connectionColor, showLandmarks, showConnections]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      className={cn('absolute inset-0 pointer-events-none', className)}
      aria-label="Pose overlay"
    />
  );
}

export default PoseOverlay;
